import { type SharedErrors } from '@lawyer/shared';

import { isApiError } from './problem.errors.js';

/**
 * Every error code, written out, pointing at the sentence that explains it.
 *
 * **Written out rather than assembled.** `errors.${code}` is one line and it is the line ADR-0019
 * forbids: a key composed at runtime is a key no gate can see, so a catalogue missing all of these
 * would pass every check in the repository and render `errors.act_forbidden` at a user. Listing
 * them makes each key a literal the parity gate reads, and `Record<ErrorCode, string>` makes a code
 * added on the server a compile error here — before it can be a missing translation in production.
 *
 * The sentences themselves are in `errors.json`, in both languages, and they are the *only* text
 * shown for a failure. `title` and `detail` from the server are English prose for a log line and
 * may be reworded at any time (`rules/api-contract.mdc` §5).
 */
export const ERROR_MESSAGE_KEY: Readonly<Record<SharedErrors.ErrorCode, string>> = {
  validation_failed: 'errors.code.validation_failed',
  acknowledgement_revision_mismatch: 'errors.code.acknowledgement_revision_mismatch',
  acknowledgement_revision_not_in_force: 'errors.code.acknowledgement_revision_not_in_force',
  act_status_transition_not_allowed: 'errors.code.act_status_transition_not_allowed',
  act_status_basis_required: 'errors.code.act_status_basis_required',
  act_link_already_exists: 'errors.code.act_link_already_exists',
  act_link_not_found: 'errors.code.act_link_not_found',
  ai_provider_in_use: 'errors.code.ai_provider_in_use',
  ai_budget_exhausted: 'errors.code.ai_budget_exhausted',
  ai_provider_timeout: 'errors.code.ai_provider_timeout',
  ai_provider_key_rejected: 'errors.code.ai_provider_key_rejected',
  ai_model_not_available: 'errors.code.ai_model_not_available',
  ai_answer_unreadable: 'errors.code.ai_answer_unreadable',
  clause_address_ambiguous: 'errors.code.clause_address_ambiguous',
  document_not_parsed: 'errors.code.document_not_parsed',
  evaluation_runs_not_comparable: 'errors.code.evaluation_runs_not_comparable',
  document_not_found: 'errors.code.document_not_found',
  document_forbidden: 'errors.code.document_forbidden',
  document_already_exists: 'errors.code.document_already_exists',
  document_file_unavailable: 'errors.code.document_file_unavailable',
  document_revision_period_conflict: 'errors.code.document_revision_period_conflict',
  official_act_reference_missing: 'errors.code.official_act_reference_missing',
  edition_check_not_eligible_act_kind: 'errors.code.edition_check_not_eligible_act_kind',
  edition_check_no_loaded_revision: 'errors.code.edition_check_no_loaded_revision',
  ai_provider_not_found: 'errors.code.ai_provider_not_found',
  ai_provider_forbidden: 'errors.code.ai_provider_forbidden',
  ai_provider_already_exists: 'errors.code.ai_provider_already_exists',
  evaluation_run_not_found: 'errors.code.evaluation_run_not_found',
  evaluation_gold_set_not_found: 'errors.code.evaluation_gold_set_not_found',
  evaluation_run_forbidden: 'errors.code.evaluation_run_forbidden',
  evaluation_run_already_exists: 'errors.code.evaluation_run_already_exists',
  answer_trace_not_found: 'errors.code.answer_trace_not_found',
  assistant_thread_not_found: 'errors.code.assistant_thread_not_found',
  assistant_thread_forbidden: 'errors.code.assistant_thread_forbidden',
  assistant_thread_already_exists: 'errors.code.assistant_thread_already_exists',
  assistant_clarification_already_answered: 'errors.code.assistant_clarification_already_answered',
  assistant_thread_full: 'errors.code.assistant_thread_full',
  assistant_thread_material_selected: 'errors.code.assistant_thread_material_selected',
  template_version_immutable: 'errors.code.template_version_immutable',
  template_not_assemblable: 'errors.code.template_not_assemblable',
  answer_trace_forbidden: 'errors.code.answer_trace_forbidden',
  answer_trace_already_exists: 'errors.code.answer_trace_already_exists',
  external_processing_not_allowed: 'errors.code.external_processing_not_allowed',
  general_mode_unavailable: 'errors.code.general_mode_unavailable',
  unauthenticated: 'errors.code.unauthenticated',
  invalid_credentials: 'errors.code.invalid_credentials',
  account_suspended: 'errors.code.account_suspended',
  registration_disabled: 'errors.code.registration_disabled',
  password_reset_token_invalid: 'errors.code.password_reset_token_invalid',
  invitation_not_valid: 'errors.code.invitation_not_valid',
  manager_cycle_detected: 'errors.code.manager_cycle_detected',
  employment_period_inverted: 'errors.code.employment_period_inverted',
  recipient_not_active: 'errors.code.recipient_not_active',
  member_not_active: 'errors.code.member_not_active',
  invalid_recipient: 'errors.code.invalid_recipient',
  not_the_owner: 'errors.code.not_the_owner',
  mail_not_configured: 'errors.code.mail_not_configured',
  route_not_found: 'errors.code.route_not_found',
  payload_too_large: 'errors.code.payload_too_large',
  vault_locked: 'errors.code.vault_locked',
  stale_version: 'errors.code.stale_version',
  idempotency_key_reuse: 'errors.code.idempotency_key_reuse',
  last_owner_required: 'errors.code.last_owner_required',
  period_locked: 'errors.code.period_locked',
  self_lockout: 'errors.code.self_lockout',
  system_role_immutable: 'errors.code.system_role_immutable',
  owner_immutable: 'errors.code.owner_immutable',
  confirmation_required: 'errors.code.confirmation_required',
  invalid_totp_code: 'errors.code.invalid_totp_code',
  totp_code_replayed: 'errors.code.totp_code_replayed',
  mfa_already_enabled: 'errors.code.mfa_already_enabled',
  reauthentication_required: 'errors.code.reauthentication_required',
  recovery_code_invalid: 'errors.code.recovery_code_invalid',
  mfa_token_expired: 'errors.code.mfa_token_expired',
  mfa_invalid_code: 'errors.code.mfa_invalid_code',
  mfa_code_replayed: 'errors.code.mfa_code_replayed',
  invitation_already_accepted: 'errors.code.invitation_already_accepted',
  rate_limited: 'errors.code.rate_limited',
  feature_disabled: 'errors.code.feature_disabled',
  service_unavailable: 'errors.code.service_unavailable',
  internal_error: 'errors.code.internal_error',
  organization_not_found: 'errors.code.organization_not_found',
  organization_forbidden: 'errors.code.organization_forbidden',
  organization_already_exists: 'errors.code.organization_already_exists',
  team_not_found: 'errors.code.team_not_found',
  team_forbidden: 'errors.code.team_forbidden',
  team_already_exists: 'errors.code.team_already_exists',
  user_not_found: 'errors.code.user_not_found',
  user_forbidden: 'errors.code.user_forbidden',
  user_already_exists: 'errors.code.user_already_exists',
  role_not_found: 'errors.code.role_not_found',
  role_forbidden: 'errors.code.role_forbidden',
  role_already_exists: 'errors.code.role_already_exists',
  invitation_not_found: 'errors.code.invitation_not_found',
  invitation_forbidden: 'errors.code.invitation_forbidden',
  invitation_already_exists: 'errors.code.invitation_already_exists',
  session_not_found: 'errors.code.session_not_found',
  session_forbidden: 'errors.code.session_forbidden',
  session_already_exists: 'errors.code.session_already_exists',
  act_not_found: 'errors.code.act_not_found',
  act_forbidden: 'errors.code.act_forbidden',
  act_already_exists: 'errors.code.act_already_exists',
  order_not_found: 'errors.code.order_not_found',
  order_forbidden: 'errors.code.order_forbidden',
  order_already_exists: 'errors.code.order_already_exists',
  acknowledgement_not_found: 'errors.code.acknowledgement_not_found',
  acknowledgement_forbidden: 'errors.code.acknowledgement_forbidden',
  acknowledgement_already_exists: 'errors.code.acknowledgement_already_exists',
  contract_not_found: 'errors.code.contract_not_found',
  contract_forbidden: 'errors.code.contract_forbidden',
  contract_already_exists: 'errors.code.contract_already_exists',
  playbook_not_found: 'errors.code.playbook_not_found',
  playbook_forbidden: 'errors.code.playbook_forbidden',
  playbook_already_exists: 'errors.code.playbook_already_exists',
  approval_not_found: 'errors.code.approval_not_found',
  approval_forbidden: 'errors.code.approval_forbidden',
  approval_already_exists: 'errors.code.approval_already_exists',
  letter_not_found: 'errors.code.letter_not_found',
  letter_forbidden: 'errors.code.letter_forbidden',
  letter_already_exists: 'errors.code.letter_already_exists',
  template_not_found: 'errors.code.template_not_found',
  template_forbidden: 'errors.code.template_forbidden',
  template_already_exists: 'errors.code.template_already_exists',
  reference_not_found: 'errors.code.reference_not_found',
  reference_forbidden: 'errors.code.reference_forbidden',
  reference_already_exists: 'errors.code.reference_already_exists',
  reference_entry_not_found: 'errors.code.reference_entry_not_found',
  reference_entry_forbidden: 'errors.code.reference_entry_forbidden',
  reference_entry_already_exists: 'errors.code.reference_entry_already_exists',
  counterparty_not_found: 'errors.code.counterparty_not_found',
  counterparty_forbidden: 'errors.code.counterparty_forbidden',
  counterparty_already_exists: 'errors.code.counterparty_already_exists',
  kb_space_not_found: 'errors.code.kb_space_not_found',
  kb_space_forbidden: 'errors.code.kb_space_forbidden',
  kb_space_already_exists: 'errors.code.kb_space_already_exists',
  kb_article_not_found: 'errors.code.kb_article_not_found',
  kb_article_forbidden: 'errors.code.kb_article_forbidden',
  kb_article_already_exists: 'errors.code.kb_article_already_exists',
  file_not_found: 'errors.code.file_not_found',
  file_forbidden: 'errors.code.file_forbidden',
  file_already_exists: 'errors.code.file_already_exists',
  report_not_found: 'errors.code.report_not_found',
  report_forbidden: 'errors.code.report_forbidden',
  report_already_exists: 'errors.code.report_already_exists',
  dashboard_not_found: 'errors.code.dashboard_not_found',
  dashboard_forbidden: 'errors.code.dashboard_forbidden',
  dashboard_already_exists: 'errors.code.dashboard_already_exists',
};

/**
 * The per-field half. `code` at the top of a problem document says the request was rejected; these
 * say which field and why, and a story that translated one and not the other would ship a form that
 * marks an input red and explains nothing.
 */
export const VALIDATION_ISSUE_MESSAGE_KEY: Readonly<
  Record<SharedErrors.ValidationIssueCode, string>
> = {
  invalid_type: 'errors.field.invalid_type',
  too_big: 'errors.field.too_big',
  too_small: 'errors.field.too_small',
  invalid_format: 'errors.field.invalid_format',
  not_multiple_of: 'errors.field.not_multiple_of',
  unrecognized_keys: 'errors.field.unrecognized_keys',
  invalid_union: 'errors.field.invalid_union',
  invalid_key: 'errors.field.invalid_key',
  invalid_element: 'errors.field.invalid_element',
  invalid_value: 'errors.field.invalid_value',
  custom: 'errors.field.custom',
};

/** What a message needs interpolated. Only `rate_limited` carries anything today. */
export interface ErrorMessage {
  readonly key: string;
  readonly values?: Readonly<Record<string, number>>;
}

/**
 * Maps a failure to the sentence that describes it, and never to text.
 *
 * Anything that is not an `ApiError` reached this point without passing the contract — a bug in the
 * bundle, a browser extension, a body that failed to parse. There is no stable code to translate,
 * and rendering the exception message would put a stack sentence in front of a user, so the generic
 * key is the answer. The detail is not lost: the error itself goes to the log.
 */
export const errorMessage = (error: unknown): ErrorMessage => {
  if (!isApiError(error)) return { key: ERROR_MESSAGE_KEY.internal_error };

  // `Retry-After` is part of the 429 response in `docs/api/openapi.yaml`, so the wait is a value the
  // contract already carries rather than a number invented here. Interpolated, not concatenated:
  // «через N с» and «in N s» put the number in different places, and a sentence glued from pieces
  // can only be right in the language it was glued for.
  return error.code === 'rate_limited' && error.retryAfterSeconds !== undefined
    ? { key: ERROR_MESSAGE_KEY.rate_limited, values: { seconds: error.retryAfterSeconds } }
    : { key: ERROR_MESSAGE_KEY[error.code] };
};

/** The key alone, for the places that render a message without interpolating anything. */
export const errorMessageKey = (error: unknown): string => errorMessage(error).key;
